import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { PROFILE } from "../../data/content";
import { ArrowDownRight } from "lucide-react";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1, delay: 0.35 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.02, 1.18]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section ref={ref} className="relative min-h-screen w-full overflow-hidden bg-[#0c2c20]">
      {/* Parallax portrait */}
      <motion.div
        style={{ y: imgY, scale: imgScale }}
        className="pointer-events-none absolute right-0 top-0 h-full w-full md:w-[46%]"
      >
        <div className="relative h-full w-full">
          <img
            src={PROFILE.headshot}
            alt="Naresh Gabriel"
            className="h-full w-full object-cover object-top opacity-70 grayscale contrast-125"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0c2c20] via-[#0c2c20]/50 to-transparent md:via-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0c2c20] via-transparent to-transparent" />
        </div>
      </motion.div>

      <motion.div
        style={{ y: textY }}
        className="relative z-10 mx-auto flex min-h-screen max-w-[1400px] flex-col justify-end px-6 pb-16 pt-32 md:px-12 md:pb-24"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="mb-8 flex items-center gap-4"
        >
          <span className="h-px w-12 bg-[#c9a24e]" />
          <span className="text-[11px] uppercase tracking-[0.4em] text-[#c9a24e]">
            {PROFILE.location}
          </span>
        </motion.div>

        <h1 data-testid="hero-name" className="font-display leading-[0.86] text-white">
          {["Naresh", "Gabriel"].map((word, i) => (
            <span key={word} className="block overflow-hidden">
              <motion.span
                custom={i}
                variants={line}
                initial="hidden"
                animate="show"
                className="block text-[18vw] font-extrabold md:text-[13vw] lg:text-[11vw]"
              >
                {word}
              </motion.span>
            </span>
          ))}
        </h1>

        <div className="mt-8 max-w-2xl">
          {PROFILE.titleLines.map((t, i) => (
            <span key={t} className="mr-3 inline-block overflow-hidden align-bottom">
              <motion.span
                custom={i + 2}
                variants={line}
                initial="hidden"
                animate="show"
                className="block font-serif-e text-xl italic text-[#a1a1aa] md:text-2xl"
              >
                {t}
                {i < PROFILE.titleLines.length - 1 && (
                  <span className="ml-3 text-[#c9a24e]">/</span>
                )}
              </motion.span>
            </span>
          ))}
        </div>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.1 }}
          className="mt-10 max-w-xl text-[15px] leading-relaxed text-[#a1a1aa] md:text-base"
        >
          {PROFILE.intro}
        </motion.p>

        <motion.button
          data-testid="hero-scroll-cta"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.9, delay: 1.3 }}
          onClick={() => document.getElementById("about")?.scrollIntoView({ behavior: "smooth" })}
          className="group mt-12 inline-flex w-fit items-center gap-3 text-[13px] uppercase tracking-[0.25em] text-white"
        >
          Explore the profile
          <span className="flex h-10 w-10 items-center justify-center rounded-full border border-white/20 transition-colors group-hover:border-[#c9a24e] group-hover:bg-[#c9a24e] group-hover:text-black">
            <ArrowDownRight size={16} />
          </span>
        </motion.button>
      </motion.div>
    </section>
  );
};
